import React, { useState } from "react";
import FrontendLayout from "../FrontendLayout";
import Alert from "../../utils/Alert";
import { Link, useNavigate } from "react-router-dom";
import { SENDOTP, REGISTERWITHOTP } from "../../data/url";
import httpClient from "../../http/HttpRequest";

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    mobile: "",
    password: "",
    name: "",
    email: "",
    otp: "",
    otpRef: "",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (otpSent && name !== "otp") {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
    } else {
      const res = await httpClient.plainRequest(REGISTERWITHOTP, formData);
      if (res.requestStatus === "OK") {
        const result = res.result;
        if (result.status === 1) {
          Alert.success(result.message);
          navigate("/login");
        } else {
          setOtpSent(false);
          Alert.error(result.message);
        }
      } else {
        setOtpSent(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number.";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required.";
    } else if (formData.password.trim().length < 8) {
      newErrors.password = "Password must be at least 8 characters long.";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    return newErrors;
  };

  const sendOtp = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
    } else {
      try {
        const res = await httpClient.plainRequest(SENDOTP, {
          mobile: formData.mobile.trim(),
        });

        if (res.requestStatus === "OK") {
          const result = res.result;
          if (result.status === 1) {
            setOtpSent(true);
            setFormData((prev) => ({
              ...prev,
              otpRef: result.ref_no,
            }));
            Alert.success(result.message);
          } else {
            Alert.error(result.message);
          }
        } else {
          Alert.error("Failed to send OTP. Please try again.");
        }
      } catch (error) {
        console.error("Error sending OTP:", error);
        Alert.error("An unexpected error occurred. Please try again later.");
      }
    }
  };

  return (
    <FrontendLayout>
      <main className="auth-page auth-register-page">
        <div className="container auth-container">
          <div className="auth-shell auth-register-shell wow fadeInUp" data-wow-delay="0.1s">
            <aside className="auth-visual auth-register-visual">
              <img src="/assets/img/feature.jpg" alt="Nexways goods transport service" />
              <div className="auth-visual-overlay"></div>
              <div className="auth-visual-content">
                <span className="auth-visual-badge">
                  <i className="fa fa-route"></i> Built for every journey
                </span>
                <h2>Move goods with confidence.</h2>
                <p>
                  Create your customer account to calculate quotes and manage
                  transport bookings with complete visibility.
                </p>
                <div className="auth-benefits">
                  <span><i className="fa fa-check"></i> Distance-based pricing</span>
                  <span><i className="fa fa-check"></i> Multiple truck options</span>
                  <span><i className="fa fa-check"></i> Simple booking receipts</span>
                </div>
              </div>
              <div className="auth-visual-footer">
                <i className="fa fa-headset"></i>
                <span>Support when you need it</span>
              </div>
            </aside>

            <section className="auth-form-panel">
              <div className="auth-form-wrap auth-register-form-wrap">
                <div className="auth-heading">
                  <span className="auth-eyebrow">Create your account</span>
                  <h1>Let&apos;s get you moving</h1>
                  <p>Complete your details and verify your mobile with OTP.</p>
                </div>

                {otpSent && (
                  <div className="auth-otp-notice">
                    <span><i className="fa fa-mobile-alt"></i></span>
                    <div>
                      <strong>OTP sent successfully</strong>
                      <small>Enter the OTP sent to +91 {formData.mobile}</small>
                    </div>
                  </div>
                )}

                <form className="auth-form auth-register-form" onSubmit={handleSubmit} noValidate>
                  <div className="auth-form-grid">
                    <div className="auth-field auth-field-full">
                      <label htmlFor="registerName">Full name</label>
                      <div className={`auth-input ${errors.name ? "has-error" : ""} ${otpSent ? "is-readonly" : ""}`}>
                        <span><i className="fa fa-user"></i></span>
                        <input
                          type="text"
                          id="registerName"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          autoComplete="name"
                          readOnly={otpSent}
                          aria-invalid={Boolean(errors.name)}
                        />
                      </div>
                      {errors.name && <small className="auth-error">{errors.name}</small>}
                    </div>

                    <div className="auth-field">
                      <label htmlFor="registerMobile">Mobile number</label>
                      <div className={`auth-input ${errors.mobile ? "has-error" : ""} ${otpSent ? "is-readonly" : ""}`}>
                        <span><i className="fa fa-phone-alt"></i></span>
                        <input
                          type="text"
                          id="registerMobile"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="10-digit number"
                          inputMode="numeric"
                          maxLength="10"
                          autoComplete="tel"
                          readOnly={otpSent}
                          aria-invalid={Boolean(errors.mobile)}
                        />
                      </div>
                      {errors.mobile && <small className="auth-error">{errors.mobile}</small>}
                    </div>

                    <div className="auth-field">
                      <label htmlFor="registerEmail">Email address</label>
                      <div className={`auth-input ${errors.email ? "has-error" : ""} ${otpSent ? "is-readonly" : ""}`}>
                        <span><i className="fa fa-envelope"></i></span>
                        <input
                          type="email"
                          id="registerEmail"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          autoComplete="email"
                          readOnly={otpSent}
                          aria-invalid={Boolean(errors.email)}
                        />
                      </div>
                      {errors.email && <small className="auth-error">{errors.email}</small>}
                    </div>

                    <div className="auth-field auth-field-full">
                      <label htmlFor="registerPassword">Create password</label>
                      <div className={`auth-input ${errors.password ? "has-error" : ""} ${otpSent ? "is-readonly" : ""}`}>
                        <span><i className="fa fa-lock"></i></span>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="registerPassword"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Minimum 8 characters"
                          autoComplete="new-password"
                          readOnly={otpSent}
                          aria-invalid={Boolean(errors.password)}
                        />
                        <button
                          type="button"
                          className="auth-password-toggle"
                          onClick={() => setShowPassword((visible) => !visible)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                        </button>
                      </div>
                      {errors.password && <small className="auth-error">{errors.password}</small>}
                    </div>

                    {otpSent && (
                      <div className="auth-field auth-field-full">
                        <label htmlFor="registerOtp">Verification OTP</label>
                        <div className="auth-input auth-otp-input">
                          <span><i className="fa fa-key"></i></span>
                          <input
                            type="text"
                            id="registerOtp"
                            name="otp"
                            value={formData.otp}
                            onChange={handleChange}
                            placeholder="Enter received OTP"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {otpSent ? (
                    <button className="auth-submit-button" type="submit">
                      Verify &amp; create account <i className="fa fa-arrow-right"></i>
                    </button>
                  ) : (
                    <button className="auth-submit-button" onClick={sendOtp} type="button">
                      Send verification OTP <i className="fa fa-paper-plane"></i>
                    </button>
                  )}
                </form>

                <div className="auth-switch">
                  <span>Already have an account?</span>
                  <Link to={"/login"}>Login here</Link>
                </div>

                <p className="auth-privacy-note">
                  <i className="fa fa-shield-alt"></i> Your personal details are used only for bookings and account support.
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </FrontendLayout>
  );
}
